import { createTheme, PaperProps, ThemeOptions, TableContainerProps, TableCellProps, TableProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { ButtonProps } from "@mui/material/Button";
import { esES } from "@mui/material/locale";

declare module "@mui/material/styles" {
    interface TypographyVariants {
        body3: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        body3: React.CSSProperties;
    }

    interface Palette {
        main1: Palette["primary"];
        main2: Palette["primary"];
        main3: Palette["primary"];
        main4: Palette["primary"];
        main5: Palette["primary"];
        main6: Palette["primary"];
        secondary1: Palette["primary"];
        secondary2: Palette["primary"];
        secondary3: Palette["primary"];
        secondary4: Palette["primary"];
        secondary5: Palette["primary"];
        secondary6: Palette["primary"];
        secondary7: Palette["primary"];
        secondary8: Palette["primary"];
        secondary9: Palette["primary"];
        secondary10: Palette["primary"];
    }

    interface PaletteOptions {
        main1: PaletteOptions["primary"];
        main2: PaletteOptions["primary"];
        main3: PaletteOptions["primary"];
        main4: PaletteOptions["primary"];
        main5: PaletteOptions["primary"];
        main6: PaletteOptions["primary"];
        secondary1: PaletteOptions["primary"];
        secondary2: PaletteOptions["primary"];
        secondary3: PaletteOptions["primary"];
        secondary4: PaletteOptions["primary"];
        secondary5: PaletteOptions["primary"];
        secondary6: PaletteOptions["primary"];
        secondary7: PaletteOptions["primary"];
        secondary8: PaletteOptions["primary"];
        secondary9: PaletteOptions["primary"];
        secondary10: PaletteOptions["primary"];
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        body3: true;
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        default: true;
        blue_outlined: true;
        action: true;
        filter_action: true;
    }

    interface ButtonPropsColorOverrides {
        main1: true;
        main2: true;
        main3: true;
        main4: true;
        main5: true;
        main6: true;
        secondary1: true;
        secondary2: true;
        secondary3: true;
        secondary4: true;
        secondary5: true;
        secondary6: true;
        secondary7: true;
        secondary8: true;
        secondary9: true;
        secondary10: true;
    }
}

declare module "@mui/material/SvgIcon" {
    interface SvgIconPropsColorOverrides {
        main1: true;
        main2: true;
        main3: true;
        main4: true;
        main5: true;
        main6: true;
        secondary1: true;
        secondary2: true;
        secondary3: true;
        secondary4: true;
        secondary5: true;
        secondary6: true;
        secondary7: true;
        secondary8: true;
        secondary9: true;
        secondary10: true;
    }
}

declare module "@mui/material/IconButton" {
    interface IconButtonPropsColorOverrides {
        main1: true;
        main2: true;
        main3: true;
        main4: true;
        main5: true;
        main6: true;
        secondary1: true;
        secondary2: true;
        secondary3: true;
        secondary4: true;
        secondary5: true;
        secondary6: true;
        secondary7: true;
        secondary8: true;
        secondary9: true;
        secondary10: true;
    }
}

declare module "@mui/material/Fab" {
    interface FabPropsColorOverrides {
        main1: true;
        main2: true;
        main3: true;
        main4: true;
        main5: true;
        main6: true;
        secondary1: true;
        secondary2: true;
        secondary3: true;
        secondary4: true;
        secondary5: true;
        secondary6: true;
        secondary7: true;
        secondary8: true;
        secondary9: true;
        secondary10: true;
    }
}

declare module "@mui/material/Paper" {
    interface PaperPropsVariantOverrides {
        section: true;
    }
}

declare module "@mui/material/MenuItem" {
    interface MenuItemPropsVariantOverrides {
        noShadow: true;
    }
}

declare module "@mui/material/Table" {
    interface TableContainerPropsVariantOverrides {
        emphasis: true;
    }
}

declare module "@mui/material/TableCell" {
    interface TableCellPropsVariantOverrides {
        first: true;
        action: true;
        internal: true;
        external: true;
    }
}

let theme: ThemeOptions = createTheme(
    {
        typography: {
            fontFamily: ["Outfit"].join(","),
            h1: {
                fontSize: 39,
                fontWeight: 300,
            },
            h2: {
                fontSize: 31,
                fontWeight: 300,
            },
            h3: {
                fontSize: 25,
                fontWeight: 300,
            },
            h4: {
                fontSize: 22,
                fontWeight: 300,
            },
            h5: {
                fontSize: 20,
                fontWeight: 300,
            },
            h6: {
                fontSize: 17,
                fontWeight: 300,
            },
            body1: {
                fontSize: 16,
                fontWeight: 300,
            },
            body2: {
                fontSize: 13,
                fontWeight: 300,
            },
            body3: {
                fontSize: 11,
                fontWeight: 300,
                fontFamily: "Outfit",
            },
        },
        components: {
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: "#A3D4E8",
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiInputBase-input": {
                            // color: "#528CD6",
                            fontWeight: 400,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#A3D4E8",
                        },
                        "& .MuiInput-underline::before": {
                            borderColor: "#A3D4E8",
                        },
                        "& .MuiFilledInput-underline::before": {
                            borderColor: "#A3D4E8",
                        },
                        "& .MuiInputLabel-asterisk": {
                            color: "#FF6060",
                        },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        color: "#FF6060",
                    },
                },
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        "& .MuiInputLabel-asterisk": {
                            color: "#FF6060",
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        // color: "#528CD6",
                        fontWeight: 400,
                        "& .MuiSelect-icon": {
                            color: "#A3D4E8",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#A3D4E8",
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        cursor: "pointer",
                        ":disabled": {
                            cursor: "not-allowed",
                            opacity: 0.4,
                            pointerEvents: "auto",
                        },
                    },
                },
            },
            MuiMenuItem: {
                variants: [
                    {
                        props: { variant: "noShadow" },
                        style: {
                            "& .Mui-selected": {
                                backgroundColor: "transparent",
                            },
                            "& .Mui-selected:hover": {
                                backgroundColor: "transparent",
                            },
                        },
                    },
                ],
            },
        },
    },
    esES,
);

theme = createTheme(theme, {
    palette: {
        main1: theme.palette.augmentColor({
            color: {
                main: "#B094FF",
            },
            name: "main1",
        }),
        main2: theme.palette.augmentColor({
            color: {
                main: "#5D5BDB",
            },
            name: "main2",
        }),
        main3: theme.palette.augmentColor({
            color: {
                main: "#5D5D5D",
            },
            name: "main3",
        }),
        main4: theme.palette.augmentColor({
            color: {
                main: "#E9AEF9",
                contrastText: "#fff",
            },
            name: "main4",
        }),
        main5: theme.palette.augmentColor({
            color: {
                main: "#E3F2F8",
            },
            name: "main5",
        }),
        main6: theme.palette.augmentColor({
            color: {
                main: "#004C89",
            },
            name: "main6",
        }),
        secondary1: theme.palette.augmentColor({
            color: {
                main: "#2C2C2C",
            },
            name: "secondary1",
        }),
        secondary2: theme.palette.augmentColor({
            color: {
                main: "#818181",
            },
            name: "secondary2",
        }),
        secondary3: theme.palette.augmentColor({
            color: {
                main: "#E35050",
            },
            name: "secondary3",
        }),
        secondary4: theme.palette.augmentColor({
            color: {
                main: "#01A388",
            },
            name: "secondary4",
        }),
        secondary5: theme.palette.augmentColor({
            color: {
                main: "#E3F2F8",
            },
            name: "secondary5",
        }),
        secondary6: theme.palette.augmentColor({
            color: {
                main: "#FFFFFF",
            },
            name: "secondary6",
        }),
        secondary7: theme.palette.augmentColor({
            color: {
                main: "#894DD8",
            },
            name: "secondary7",
        }),
        secondary8: theme.palette.augmentColor({
            color: {
                main: "#613797",
            },
            name: "secondary8",
        }),
        secondary9: theme.palette.augmentColor({
            color: {
                main: "#F79E18",
                contrastText: "#fff",
            },
            name: "secondary9",
        }),
        secondary10: theme.palette.augmentColor({
            color: {
                main: "#BB3545",
                contrastText: "#fff",
            },
            name: "secondary10",
        }),
    },
});

export const FinbraTheme = createTheme(theme, {
    components: {
        MuiButton: {
            styleOverrides: {
                root: (t: any) => {
                    let color = theme.palette[t.ownerState.color].contrastText;
                    if (t.classes?.root.includes("MuiButton-loadingPositionCenter")) {
                        color = "transparent";
                    }

                    return {
                        "&.MuiButton-root": {
                            borderRadius: 6,
                            textTransform: "none",
                            fontSize: 16,
                            fontWeight: 700,
                            lineHeight: "22px",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: theme.palette[t.ownerState.color].main,
                            color: color,
                            opacity: 0.4,
                            boxShadow: "none",
                        },
                        "&.MuiButton-loadingIndicator": {
                            color: theme.palette[t.ownerState.color].contrastText,
                        },
                    };
                },
            },
            variants: [
                {
                    props: {
                        variant: "blue_outlined",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: ButtonProps }) => {
                        return {
                            color: "#002652",
                            backgroundColor: "#FFF",
                            border: "2px solid #002652",
                            lineHeight: "22px",
                            "&.Mui-disabled": {
                                color: "#bdbdbd",
                                border: "2px solid #bdbdbd",
                                backgroundColor: "transparent",
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "filter_action",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: ButtonProps }) => {
                        return {
                            minHeight: "40px !important",
                            width: "130px !important",
                            maxWidth: "130px",
                            color: theme.palette[ownerState.color].contrastText,
                            backgroundColor: theme.palette[ownerState.color].main,
                            "&:hover": {
                                backgroundColor: theme.palette[ownerState.color].dark,
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "action",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: ButtonProps }) => {
                        return {
                            minHeight: "65px !important",
                            [theme.breakpoints.up("xs")]: {
                                width: "100%",
                            },
                            [theme.breakpoints.up("sm")]: {
                                maxWidth: "130px",
                            },
                            [theme.breakpoints.up("md")]: {
                                maxWidth: "130px",
                            },
                            color: theme.palette[ownerState.color].contrastText,
                            backgroundColor: theme.palette[ownerState.color].main,
                            "&:hover": {
                                backgroundColor: theme.palette[ownerState.color].dark,
                            },
                        };
                    },
                },
            ],
        },
        MuiPaper: {
            variants: [
                {
                    props: {
                        variant: "section",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: PaperProps }) => {
                        return {
                            padding: "1em !important",
                            borderRadius: "14px",
                            // boxShadow: theme.shadows[ownerState.elevation ?? 1],
                            boxShadow: "0px 5.5px 5.5px 0px rgba(0, 0, 0, 0.05);",
                        };
                    },
                },
            ],
        },
        MuiTableContainer: {
            styleOverrides: {
                root: (t: any) => {
                    return {
                        "&.MuiTableContainer-root": {
                            borderTopLeftRadius: "14px",
                            borderTopRightRadius: "14px",
                        },
                        "& tbody tr:nth-of-type(even)": {
                            backgroundColor: "rgba(227, 242, 248, 0.33)",
                        },
                        "& tbody tr td": {
                            borderBottom: "0px",
                        },
                        "& tbody tr td:not(:first-of-type)": {
                            borderLeft: "1px solid #A3D4E8",
                        },
                        "& tbody tr td:not(:last-of-type)": {
                            borderRight: "1px solid #A3D4E8",
                        },
                    };
                },
            },
            variants: [
                {
                    props: {
                        variant: "emphasis",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableProps }) => {
                        let color = theme.palette["main3"].contrastText;
                        return {
                            "& thead tr th": {
                                backgroundColor: theme.palette["main3"].main,
                            },
                            "& thead tr th:not(:last-of-type)::after": {
                                content: "''",
                                position: "absolute",
                                top: "12px",
                                bottom: "0",
                                right: 0,
                                width: "2px",
                                backgroundColor: "#E3F2F8",
                            },
                            "& thead th": {
                                color: color,
                                textAlign: "center",
                                fontWeight: 600,
                                fontSize: "18px",
                                backgroundColor: "#E3F2F8",
                                // backgroundColor: `${theme.palette["main3"].main} !important`,
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "docs",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableProps }) => {
                        return {
                            "&.MuiTableContainer-root": {
                                borderTop: "1px solid #A3D4E8",
                                borderLeft: "1px solid #A3D4E8",
                                borderRight: "1px solid #A3D4E8",
                                borderBottom: "1px solid #A3D4E8",

                                borderBottomLeftRadius: "14px",
                                borderBottomRightRadius: "14px",
                            },
                            "& thead tr th": {
                                backgroundColor: `#E3F2F8 !important`,
                                padding: "3px 6px",
                                borderBottom: "1px solid #A3D4E8",
                            },
                            // "& thead tr th:first-of-type": {
                            //   color: theme.palette["main3"].main,
                            //     textAlign: "left",
                            //     fontWeight: 600,
                            //     fontSize: "15px",
                            //     paddingLeft: "10px",
                            // },
                            "& thead tr th:not(:first-of-type)": {
                                textAlign: "center",
                                width: "70px",
                                fontSize: "11px",
                                fontWeight: 500,
                                color: "#528CD6",
                                borderLeft: "1px solid #A3D4E8",
                                lineHeight: "1rem",
                            },
                            "& tbody tr td": {
                                padding: "0px 6px",
                            },
                            "& tbody tr:not(:last-of-type)": {
                                borderBottom: "1px solid #A3D4E8",
                            },
                        };
                    },
                },
            ],
        },
        MuiTableCell: {
            variants: [
                {
                    props: {
                        variant: "first",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableCellProps }) => {
                        return {
                            "&.MuiTableCell-root": {
                                paddingLeft: "15px",
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "action",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableCellProps }) => {
                        return {
                            "&.MuiTableCell-root": {
                                textAlign: "center",
                                width: "70px",
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "internal",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableCellProps }) => {
                        return {
                            "&.MuiTableCell-root": {
                                color: theme.palette["main2"].main,
                                textAlign: "left",
                                fontWeight: 600,
                                fontSize: "15px",
                                paddingLeft: "10px",
                            },
                        };
                    },
                },
                {
                    props: {
                        variant: "external",
                    },
                    style: ({ theme, ownerState }: { theme: Theme; ownerState: TableCellProps }) => {
                        return {
                            "&.MuiTableCell-root": {
                                color: theme.palette["main3"].main,
                                textAlign: "left",
                                fontWeight: 600,
                                fontSize: "15px",
                                paddingLeft: "10px",
                            },
                        };
                    },
                },
            ],
        },
    },
});
